/**
 * i18n 核心模块
 *
 * 方案 B（最小化）：t() 函数 + 语言文件 + location.reload() 切换。
 * 与 theme-service 切换模式一致。
 *
 * 用法：
 *   import { t, getLocale, setLocale } from './i18n';
 *   t("battle.give_up")  // → "← 放弃 GIVE UP" (zh-CN) 或 "← GIVE UP" (en)
 */

import zhCN from './locales/zh-CN';
import en from './locales/en';
import type { TranslationKey, Locale } from './types';

export type { TranslationKey, Locale } from './types';

const STORAGE_KEY = 'battlecode-locale';

/** 所有翻译 map */
const translations: Record<Locale, Record<TranslationKey, string>> = {
  'zh-CN': zhCN,
  'en': en,
};

/** 检测用户首选语言 */
function detectLocale(): Locale {
  try {
    // 1. localStorage 持久化优先
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en') return 'en';
    if (stored === 'zh-CN') return 'zh-CN';
  } catch {
    // localStorage 不可用时不崩溃
  }

  // 2. 浏览器语言检测
  if (typeof navigator !== 'undefined' && navigator.language) {
    const lang = navigator.language.toLowerCase();
    if (lang.startsWith('zh')) return 'zh-CN';
  }

  // 3. 默认中文
  return 'zh-CN';
}

let currentLocale: Locale = detectLocale();

/** 获取当前语言 */
export function getLocale(): Locale {
  return currentLocale;
}

/**
 * 翻译函数
 * @param key 翻译 key（编译时类型安全）
 * @returns 当前语言对应的翻译文本
 */
export function t(key: TranslationKey): string {
  const map = translations[currentLocale];
  const val = map[key];
  // key 不存在于当前语言文件时，编译时可以检查到（TranslationKey 联合类型），
  // 但运行时如果 en.ts 漏 key 会回退 zh-CN
  if (val === undefined) {
    console.warn(`[i18n] Missing translation: "${key}" for locale "${currentLocale}"`);
    return translations['zh-CN'][key] ?? key;
  }
  return val;
}

/**
 * 设置语言并刷新页面
 * @param locale 目标语言
 */
export function setLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore
  }
  // 方案 B：location.reload() 让所有 screen 重新 render，保证一次性切换
  window.location.reload();
}
